/**
 * useProject Hook
 *
 * Manages multi-unit development projects:
 * - Create and manage projects (subdivisions, townhomes, apartments)
 * - Add/manage segments (phases, buildings, blocks)
 * - Add/manage units (lots, townhomes, apartments)
 * - Link jobs to projects and units
 * - Track project progress
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  query,
  orderBy,
  where,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import {
  createProject as createProjectFn,
  updateProject as updateProjectFn,
  softDeleteProject,
  createSegment as createSegmentFn,
  updateSegment as updateSegmentFn,
  deleteSegment as deleteSegmentFn,
  createUnit as createUnitFn,
  batchCreateUnits as batchCreateUnitsFn,
  updateUnit as updateUnitFn,
  deleteUnit as deleteUnitFn,
  getProjects,
  getSegments,
  getUnits,
  getProjectJobs,
  recomputeProjectStats,
  linkJobToProject as linkJobToProjectFn,
} from '@/lib/firestore-project-helpers';
import type {
  DevelopmentProject,
  CreateDevelopmentProjectInput,
  UpdateDevelopmentProjectInput,
  ProjectSegment,
  Unit,
  CreateUnitInput,
  UnitStats,
  SharedPermitStats,
  JobScopeType,
} from '@/types/project';

// ============================================
// PROJECTS LIST HOOK
// ============================================

interface UseProjectsReturn {
  projects: DevelopmentProject[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createProject: (input: CreateDevelopmentProjectInput) => Promise<string>;
  deleteProject: (projectId: string) => Promise<void>;
}

/**
 * Hook for managing the list of all projects
 */
export function useProjects(): UseProjectsReturn {
  const { user, profile } = useAuth();
  const [projects, setProjects] = useState<DevelopmentProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const contractorId = profile?.contractorId || user?.uid;

  // Subscribe to projects collection
  useEffect(() => {
    if (!contractorId) {
      setProjects([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const projectsRef = collection(db, `contractors/${contractorId}/projects`);
    // Use simpler query - filter deleted items client-side to avoid != operator issues with subcollections
    const q = query(
      projectsRef,
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const projectsData: DevelopmentProject[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Filter out deleted projects client-side
          if (!data.isDeleted) {
            projectsData.push({
              id: doc.id,
              ...data,
            } as DevelopmentProject);
          }
        });
        setProjects(projectsData);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [contractorId]);

  // Refresh projects
  const refresh = useCallback(async () => {
    if (!contractorId) return;
    setIsLoading(true);
    try {
      const snapshot = await getProjects(contractorId);
      const projectsData: DevelopmentProject[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.isDeleted) {
          projectsData.push({
            id: doc.id,
            ...data,
          } as DevelopmentProject);
        }
      });
      setProjects(projectsData);
    } catch (err) {
      console.error('Error refreshing projects:', err);
      setError('Failed to refresh projects');
    } finally {
      setIsLoading(false);
    }
  }, [contractorId]);

  // Create a new project
  const createProject = useCallback(async (
    input: CreateDevelopmentProjectInput
  ): Promise<string> => {
    if (!contractorId || !user) {
      throw new Error('Not authenticated');
    }
    return createProjectFn(contractorId, user.uid, input);
  }, [contractorId, user]);

  // Delete a project (soft delete)
  const deleteProject = useCallback(async (projectId: string): Promise<void> => {
    if (!contractorId || !user) {
      throw new Error('Not authenticated');
    }
    await softDeleteProject(contractorId, projectId, user.uid);
  }, [contractorId, user]);

  return {
    projects,
    isLoading,
    error,
    refresh,
    createProject,
    deleteProject,
  };
}

// ============================================
// SINGLE PROJECT HOOK
// ============================================

interface UseProjectReturn {
  project: DevelopmentProject | null;
  segments: ProjectSegment[];
  units: Unit[];
  isLoading: boolean;
  error: string | null;

  // Project actions
  updateProject: (updates: UpdateDevelopmentProjectInput) => Promise<void>;
  refreshStats: () => Promise<void>;

  // Segment actions
  createSegment: (segment: Omit<ProjectSegment, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateSegment: (segmentId: string, updates: Partial<ProjectSegment>) => Promise<void>;
  deleteSegment: (segmentId: string) => Promise<void>;

  // Unit actions
  createUnit: (input: CreateUnitInput) => Promise<string>;
  batchCreateUnits: (units: CreateUnitInput[]) => Promise<string[]>;
  updateUnit: (unitId: string, updates: Partial<Unit>) => Promise<void>;
  deleteUnit: (unitId: string) => Promise<void>;

  // Job linkage
  linkJobToProject: (jobId: string, options: {
    unitId?: string;
    jobScopeType: JobScopeType;
    templateJobId?: string;
    isTemplate?: boolean;
  }) => Promise<void>;
}

/**
 * Hook for managing a single project with its segments and units
 */
export function useProject(projectId: string | undefined): UseProjectReturn {
  const { user, profile } = useAuth();
  const [project, setProject] = useState<DevelopmentProject | null>(null);
  const [segments, setSegments] = useState<ProjectSegment[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const contractorId = profile?.contractorId || user?.uid;

  // Subscribe to project document
  useEffect(() => {
    if (!contractorId || !projectId) {
      setProject(null);
      setSegments([]);
      setUnits([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const projectRef = doc(db, `contractors/${contractorId}/projects/${projectId}`);

    const unsubscribeProject = onSnapshot(
      projectRef,
      (doc) => {
        if (doc.exists()) {
          setProject({
            id: doc.id,
            ...doc.data(),
          } as DevelopmentProject);
        } else {
          setProject(null);
          setError('Project not found');
        }
        setIsLoading(false);
      },
      (err) => {
        console.error('Error fetching project:', err);
        setError('Failed to load project');
        setIsLoading(false);
      }
    );

    return () => unsubscribeProject();
  }, [contractorId, projectId]);

  // Subscribe to segments
  useEffect(() => {
    if (!contractorId || !projectId) {
      setSegments([]);
      return;
    }

    const segmentsRef = collection(db, `contractors/${contractorId}/projects/${projectId}/segments`);
    const q = query(segmentsRef, orderBy('sortOrder', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const segmentsData: ProjectSegment[] = [];
        snapshot.forEach((doc) => {
          segmentsData.push({
            id: doc.id,
            ...doc.data(),
          } as ProjectSegment);
        });
        setSegments(segmentsData);
      },
      (err) => {
        console.error('Error fetching segments:', err);
      }
    );

    return () => unsubscribe();
  }, [contractorId, projectId]);

  // Subscribe to units
  useEffect(() => {
    if (!contractorId || !projectId) {
      setUnits([]);
      return;
    }

    const unitsRef = collection(db, `contractors/${contractorId}/projects/${projectId}/units`);
    const q = query(unitsRef, orderBy('sortOrder', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const unitsData: Unit[] = [];
        snapshot.forEach((doc) => {
          unitsData.push({
            id: doc.id,
            ...doc.data(),
          } as Unit);
        });
        setUnits(unitsData);
      },
      (err) => {
        console.error('Error fetching units:', err);
      }
    );

    return () => unsubscribe();
  }, [contractorId, projectId]);

  // Update project
  const updateProject = useCallback(async (
    updates: UpdateDevelopmentProjectInput
  ): Promise<void> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    await updateProjectFn(contractorId, projectId, updates);
  }, [contractorId, projectId]);

  // Refresh stats
  const refreshStats = useCallback(async (): Promise<void> => {
    if (!contractorId || !projectId) return;
    await recomputeProjectStats(contractorId, projectId);
  }, [contractorId, projectId]);

  // Segment actions
  const createSegment = useCallback(async (
    segment: Omit<ProjectSegment, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>
  ): Promise<string> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    return createSegmentFn(contractorId, projectId, segment);
  }, [contractorId, projectId]);

  const updateSegment = useCallback(async (
    segmentId: string,
    updates: Partial<ProjectSegment>
  ): Promise<void> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    await updateSegmentFn(contractorId, projectId, segmentId, updates);
  }, [contractorId, projectId]);

  const deleteSegment = useCallback(async (segmentId: string): Promise<void> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    await deleteSegmentFn(contractorId, projectId, segmentId);
  }, [contractorId, projectId]);

  // Unit actions
  const createUnit = useCallback(async (input: CreateUnitInput): Promise<string> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    return createUnitFn(contractorId, projectId, input);
  }, [contractorId, projectId]);

  const batchCreateUnits = useCallback(async (unitsInput: CreateUnitInput[]): Promise<string[]> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    return batchCreateUnitsFn(contractorId, projectId, unitsInput);
  }, [contractorId, projectId]);

  const updateUnit = useCallback(async (
    unitId: string,
    updates: Partial<Unit>
  ): Promise<void> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    await updateUnitFn(contractorId, projectId, unitId, updates);
  }, [contractorId, projectId]);

  const deleteUnit = useCallback(async (unitId: string): Promise<void> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    await deleteUnitFn(contractorId, projectId, unitId);
  }, [contractorId, projectId]);

  // Link job to project
  const linkJobToProject = useCallback(async (
    jobId: string,
    options: {
      unitId?: string;
      jobScopeType: JobScopeType;
      templateJobId?: string;
      isTemplate?: boolean;
    }
  ): Promise<void> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    await linkJobToProjectFn(contractorId, jobId, projectId, options);
  }, [contractorId, projectId]);

  return {
    project,
    segments,
    units,
    isLoading,
    error,
    updateProject,
    refreshStats,
    createSegment,
    updateSegment,
    deleteSegment,
    createUnit,
    batchCreateUnits,
    updateUnit,
    deleteUnit,
    linkJobToProject,
  };
}

// ============================================
// PROJECT JOBS HOOK
// ============================================

interface ProjectJob {
  id: string;
  title: string;
  status: string;
  permitType?: string;
  complianceStatus?: string;
  unitId?: string;
  jobScopeType?: JobScopeType;
}

interface UseProjectJobsReturn {
  jobs: ProjectJob[];
  unitJobs: Map<string, ProjectJob[]>;  // Grouped by unitId
  sharedJobs: ProjectJob[];             // Jobs with shared_common or campus_site scope
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching jobs linked to a project
 */
export function useProjectJobs(projectId: string | undefined): UseProjectJobsReturn {
  const { user, profile } = useAuth();
  const [jobs, setJobs] = useState<ProjectJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const contractorId = profile?.contractorId || user?.uid;

  const fetchJobs = useCallback(async () => {
    if (!contractorId || !projectId) {
      setJobs([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const snapshot = await getProjectJobs(contractorId, projectId);
      const jobsData: ProjectJob[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        jobsData.push({
          id: doc.id,
          title: data.title,
          status: data.status,
          permitType: data.permitType,
          complianceStatus: data.complianceStatus || data.compliance?.status,
          unitId: data.unitId,
          jobScopeType: data.jobScopeType,
        });
      });
      setJobs(jobsData);
    } catch (err) {
      console.error('Error fetching project jobs:', err);
      setError('Failed to load project jobs');
    } finally {
      setIsLoading(false);
    }
  }, [contractorId, projectId]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Group jobs by unit
  const unitJobs = useMemo(() => {
    const grouped = new Map<string, ProjectJob[]>();
    jobs.forEach((job) => {
      if (job.unitId && job.jobScopeType === 'unit_specific') {
        const existing = grouped.get(job.unitId) || [];
        existing.push(job);
        grouped.set(job.unitId, existing);
      }
    });
    return grouped;
  }, [jobs]);

  // Filter shared jobs
  const sharedJobs = useMemo(() => {
    return jobs.filter((job) =>
      job.jobScopeType === 'shared_common' || job.jobScopeType === 'campus_site'
    );
  }, [jobs]);

  return {
    jobs,
    unitJobs,
    sharedJobs,
    isLoading,
    error,
    refresh: fetchJobs,
  };
}

// ============================================
// BATCH CREATE JOBS HOOK
// ============================================

export interface BatchCreateJobsInput {
  projectId: string;
  unitIds: string[];
  templateJobId?: string;
  permitType: string;
  tradeType?: string;
  jobScopeType: 'unit_specific' | 'shared_common';
  title?: string;
  description?: string;
}

interface BatchCreateJobsResult {
  success: boolean;
  jobIds: string[];
  count: number;
}

interface UseBatchCreateJobsReturn {
  batchCreateJobs: (input: BatchCreateJobsInput) => Promise<BatchCreateJobsResult>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for batch creating jobs for multiple units
 */
export function useBatchCreateJobs(): UseBatchCreateJobsReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const batchCreateJobs = useCallback(async (
    input: BatchCreateJobsInput
  ): Promise<BatchCreateJobsResult> => {
    setIsLoading(true);
    setError(null);

    try {
      const batchCreateJobsFn = httpsCallable<BatchCreateJobsInput, BatchCreateJobsResult>(
        functions,
        'batchCreateJobs'
      );

      const result = await batchCreateJobsFn(input);
      return result.data;
    } catch (err: any) {
      const message = err?.message || 'Failed to create jobs';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    batchCreateJobs,
    isLoading,
    error,
  };
}

// ============================================
// DEVELOPMENT PROCESS STEPS HOOK
// ============================================

export interface DevelopmentProcessStep {
  id: string;
  jurisdictionId: number;
  projectTypeCode: string;
  stepNumber: number;
  stepName: string;
  description?: string;
  required: boolean;
  typicalDuration?: string;
  responsibleParty?: string;
  deliverables?: string[];
  agencyReviews?: string[];
  prerequisites?: string[];
  tips?: string[];
  sourceReference?: string;
}

interface UseDevelopmentProcessStepsReturn {
  steps: DevelopmentProcessStep[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching development process steps for a project type and jurisdiction
 */
export function useDevelopmentProcessSteps(
  projectTypeCode: string | undefined,
  jurisdictionId: number | undefined
): UseDevelopmentProcessStepsReturn {
  const [steps, setSteps] = useState<DevelopmentProcessStep[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSteps = useCallback(async () => {
    if (!projectTypeCode) {
      setSteps([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getDevelopmentProcessSteps = httpsCallable<
        { projectTypeCode: string; jurisdictionId?: number },
        { success: boolean; steps: DevelopmentProcessStep[] }
      >(functions, 'getDevelopmentProcessSteps');

      const result = await getDevelopmentProcessSteps({
        projectTypeCode,
        jurisdictionId,
      });

      setSteps(result.data.steps || []);
    } catch (err: any) {
      console.error('Error fetching process steps:', err);
      setError(err?.message || 'Failed to fetch process steps');
      setSteps([]);
    } finally {
      setIsLoading(false);
    }
  }, [projectTypeCode, jurisdictionId]);

  useEffect(() => {
    fetchSteps();
  }, [fetchSteps]);

  return {
    steps,
    isLoading,
    error,
    refetch: fetchSteps,
  };
}

// ============================================
// PROJECT DOCUMENTS HOOK
// ============================================

import type {
  ProjectDocument,
  ProjectDocumentScope,
  ProjectDocumentCategory,
  CreateProjectDocumentInput,
} from '@/types/project';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import {
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  Timestamp as FSTimestamp,
} from 'firebase/firestore';

interface UseProjectDocumentsReturn {
  documents: ProjectDocument[];
  projectWideDocuments: ProjectDocument[];
  unitDocuments: Map<string, ProjectDocument[]>;
  segmentDocuments: Map<string, ProjectDocument[]>;
  isLoading: boolean;
  error: string | null;
  uploadDocument: (input: CreateProjectDocumentInput, onProgress?: (progress: number) => void) => Promise<string>;
  deleteDocument: (documentId: string) => Promise<void>;
  archiveDocument: (documentId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook for managing project documents
 */
export function useProjectDocuments(projectId: string | undefined): UseProjectDocumentsReturn {
  const { user, profile } = useAuth();
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const contractorId = profile?.contractorId || user?.uid;

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    if (!contractorId || !projectId) {
      setDocuments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const docsRef = collection(db, `contractors/${contractorId}/projects/${projectId}/documents`);
      const q = query(
        docsRef,
        where('status', '!=', 'archived'),
        orderBy('status'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const docsData: ProjectDocument[] = [];
      snapshot.forEach((doc) => {
        docsData.push({
          id: doc.id,
          ...doc.data(),
        } as ProjectDocument);
      });
      setDocuments(docsData);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }, [contractorId, projectId]);

  // Subscribe to documents
  useEffect(() => {
    if (!contractorId || !projectId) {
      setDocuments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const docsRef = collection(db, `contractors/${contractorId}/projects/${projectId}/documents`);
    const q = query(docsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docsData: ProjectDocument[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.status !== 'archived') {
            docsData.push({
              id: docSnap.id,
              ...data,
            } as ProjectDocument);
          }
        });
        setDocuments(docsData);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error fetching documents:', err);
        setError('Failed to load documents');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [contractorId, projectId]);

  // Group documents by scope
  const projectWideDocuments = useMemo(() => {
    return documents.filter((doc) => doc.scope === 'project_wide');
  }, [documents]);

  const unitDocuments = useMemo(() => {
    const grouped = new Map<string, ProjectDocument[]>();
    documents.forEach((doc) => {
      if (doc.scope === 'unit_specific' && doc.unitId) {
        const existing = grouped.get(doc.unitId) || [];
        existing.push(doc);
        grouped.set(doc.unitId, existing);
      }
    });
    return grouped;
  }, [documents]);

  const segmentDocuments = useMemo(() => {
    const grouped = new Map<string, ProjectDocument[]>();
    documents.forEach((doc) => {
      if (doc.scope === 'segment_specific' && doc.segmentId) {
        const existing = grouped.get(doc.segmentId) || [];
        existing.push(doc);
        grouped.set(doc.segmentId, existing);
      }
    });
    return grouped;
  }, [documents]);

  // Upload document
  const uploadDocument = useCallback(async (
    input: CreateProjectDocumentInput,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    if (!contractorId || !projectId || !user) {
      throw new Error('Not authenticated');
    }

    const { file, name, description, category, scope, unitId, segmentId, tags } = input;

    // Create storage path
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `contractors/${contractorId}/projects/${projectId}/documents/${timestamp}_${safeName}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress?.(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            // Get download URL
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

            // Create document record in Firestore
            const docsRef = collection(db, `contractors/${contractorId}/projects/${projectId}/documents`);
            const docData: Omit<ProjectDocument, 'id'> = {
              projectId,
              scope,
              unitId: scope === 'unit_specific' ? unitId : undefined,
              segmentId: scope === 'segment_specific' ? segmentId : undefined,
              name,
              description,
              category,
              fileName: file.name,
              filePath: storagePath,
              fileSize: file.size,
              fileType: file.type,
              downloadUrl,
              uploadedBy: user.uid,
              uploadedByName: user.displayName || user.email || 'Unknown',
              tags,
              version: 1,
              isLatest: true,
              status: 'active',
              createdAt: FSTimestamp.now(),
            };

            const docRef = await addDoc(docsRef, docData);
            resolve(docRef.id);
          } catch (err) {
            console.error('Error saving document record:', err);
            reject(err);
          }
        }
      );
    });
  }, [contractorId, projectId, user]);

  // Delete document (soft delete - sets status to archived)
  const deleteDocument = useCallback(async (documentId: string): Promise<void> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    const docRef = doc(db, `contractors/${contractorId}/projects/${projectId}/documents/${documentId}`);
    await deleteDoc(docRef);
  }, [contractorId, projectId]);

  // Archive document
  const archiveDocument = useCallback(async (documentId: string): Promise<void> => {
    if (!contractorId || !projectId) {
      throw new Error('No project selected');
    }
    const docRef = doc(db, `contractors/${contractorId}/projects/${projectId}/documents/${documentId}`);
    await updateDoc(docRef, {
      status: 'archived',
      updatedAt: FSTimestamp.now(),
    });
  }, [contractorId, projectId]);

  return {
    documents,
    projectWideDocuments,
    unitDocuments,
    segmentDocuments,
    isLoading,
    error,
    uploadDocument,
    deleteDocument,
    archiveDocument,
    refresh: fetchDocuments,
  };
}
